import { useIntl } from "react-intl";
import { useCompanyContext } from "@/pages/company/use-company-context";
import { useMe } from "@/auth/use-me";
import { actingAsDeclaration } from "@/lib/share-item";
import { projectRights, type ProjectRights, type ReadOnlyReason } from "@/lib/project-rights";
import { projectWriteError } from "@/lib/project-write-error";
import { useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { throwIfValidationRejected, useValidationFallback } from "@/components/match/use-match-editor";
import {
  DeleteProjectDocument,
  LinkProjectMatchDocument,
  ProjectDocument,
  UnlinkProjectMatchDocument,
  UpdateProjectDocument,
  type ProjectQuery,
} from "@/graphql/generated/operations";
import { useListingSource } from "@/pages/share/listings/use-listing-source";
import type { DashboardFacetKey, DashboardKind } from "./dashboard-kinds";
import { parseCandidates, parseDocuments, type PlanPerson } from "./project-plan";

// Woher das Dashboard sein Objekt hat, und wohin es schreibt: ein Port mit je einem Adapter je Art.
// Die Hülle (ProjectDashboard) kennt nur diesen Vertrag; welche Abfrage und welche Mutation dahinter
// stehen, weiß allein der Adapter. Ein Projekt der Art LISTING liest und schreibt genesis nur über
// `Listing`, nie über `Project` (@external-adr GENESIS-0070-a-listing-is-a-kind-of-project-not-a-shared-item §2).

/** Das lose `data`-Dokument (JSONB). Getippt wird erst dort, wo eine Fläche es liest (project-plan.ts). */
export type DashboardData = Readonly<Record<string, unknown>>;

export interface DashboardActions {
  canUpdate: boolean;
  canManageAccess: boolean;
  canDelete: boolean;
  /** Matches verknüpfen und lösen. Bei manchen Arten unabhängig von `canUpdate`. */
  canManageMatches: boolean;
  /**
   * Nur bei Ausschreibungen: die Bewerbungen lesen. Mitgliedschaft allein reicht dafür nicht
   * (@external-adr GENESIS-0096-listing-applications-are-read-only-by-their-explicit-readers).
   */
  canReadApplications?: boolean;
  canChangeSearchTarget?: boolean;
}

export interface DashboardEntity {
  company?: { id: string; name: string } | null;
  readOnlyReason?: ReadOnlyReason;
  actingAsCompanyName?: string | null;
  id: string;
  name: string;
  description: string;
  data: DashboardData;
  createdAt: string | null;
  /** Die erstellende Person; null, wo Genesis sie nicht verrät (Nicht-Mitglieder einer Ausschreibung). */
  createdBy: PlanPerson | null;
  /** Menschen mit Zugriff, bei persönlichen Projekten ohne die Eigentümerin. */
  members: readonly PlanPerson[];
  /** Eingetragen (Zugriffszeile vorhanden). Nur Mitglieder sehen die Mitglieder-Flächen. */
  isMember: boolean;
  actions: DashboardActions;
  matchIds: readonly string[];
  /** Flächen, die nach dem gespeicherten Stand schon Inhalt haben, auch ohne Eintrag in `data.sections`. */
  filledFacets: readonly DashboardFacetKey[];
}

/** Ein Ausschnitt fürs Speichern. `data` wird von genesis in den Bestand gemischt, nicht ersetzt. */
export interface DashboardPatch {
  name?: string;
  description?: string;
  data?: DashboardData;
}

export interface DashboardSource {
  actingAsCompanyId?: string | null;
  loading: boolean;
  error: unknown;
  entity: DashboardEntity | null;
  /** Wirft bei Ablehnung; die Hülle zeigt das als Speicherfehler mit Wiederholen. */
  save: (patch: DashboardPatch) => Promise<void>;
  remove: () => Promise<void>;
  /** Nur wo die Art Matches verknüpfen kann. Fehlt es, bleibt die Fläche "Match" verborgen. */
  linkMatch?: (matchId: string) => Promise<void>;
  unlinkMatch?: (matchId: string) => Promise<void>;
}

/**
 * Wählt den Adapter zur Art. Beide Adapter laufen immer mit, der unbeteiligte ohne Id und damit ohne
 * Abfrage: so ruft die Hülle in jedem Render dieselben Hooks auf.
 */
export function useDashboardSource(kind: DashboardKind, id: string): DashboardSource {
  const project = useProjectSource(kind === "PROJECT" ? id : "");
  const listing = useListingSource(kind === "LISTING" ? id : "");
  return kind === "LISTING" ? listing : project;
}

type LoadedProject = NonNullable<ProjectQuery["project"]>;

function personName(person: { name?: { given?: readonly string[] | null; family?: string | null } | null }): string {
  return [person.name?.given?.join(" "), person.name?.family].filter(Boolean).join(" ");
}

function projectEntity(project: LoadedProject, rights: ProjectRights): DashboardEntity {
  const creator = project.createdBy;
  const createdBy: PlanPerson | null = creator
    ? {
        id: creator.id,
        name: personName(creator),
        avatarContent: creator.avatar?.content,
        avatarMime: creator.avatar?.mimeType,
      }
    : null;
  const data = (project.data ?? {}) as DashboardData;
  const filledFacets: DashboardFacetKey[] = [];
  // Wer Menschen im Projekt hat, hat ein Team, auch wenn die Fläche nie als Pille geholt wurde.
  if (parseCandidates(data.candidates).length > 0) filledFacets.push("mitglieder");
  if (parseDocuments(data.documents).length > 0) filledFacets.push("dokumente");
  return {
    id: project.id,
    company: project.company,
    readOnlyReason: rights.readOnlyReason,
    name: project.name ?? "",
    description: project.description ?? "",
    data,
    createdAt: project.createdAt ?? null,
    createdBy,
    // Nur bei persönlichen Projekten steht die Eigentümerin außerhalb der Mitgliederliste.
    members: (project.access ?? []).flatMap((entry) =>
      entry?.person && (project.company || entry.person.id !== creator?.id)
        ? [
            {
              id: entry.person.id,
              name: entry.person.formattedName ?? "",
              avatarContent: entry.person.avatar?.content,
              avatarMime: entry.person.avatar?.mimeType,
            },
          ]
        : [],
    ),
    isMember: project.access != null,
    // Ein Match zu verknüpfen verlangt bei einem Projekt Schreibrecht auf den Plan, also dasselbe wie
    // jede andere Änderung (linkProjectMatch).
    actions: { ...rights, canManageMatches: rights.canUpdate },
    matchIds: (project.matches ?? []).map((match) => match.id),
    filledFacets,
  };
}

/** Adapter für Projekte der Art PROJECT: Query `Project`, Mutationen update/delete/link/unlink. */
export function useProjectSource(id: string): DashboardSource {
  const intl = useIntl();
  const me = useMe();
  const { active: activeCompany } = useCompanyContext();
  const actingAsCompanyId = activeCompany?.canAdminister ? activeCompany.id : null;
  // Genesis lehnt eine Eingabe nicht mit einem Transportfehler ab, sondern mit einer zweiten
  // Ausprägung der Ergebnis-Union; Apollo wirft dafür nicht.
  const validationFallback = useValidationFallback();
  const query = useQuery(ProjectDocument, { variables: { id }, skip: !id });
  const [updateProject] = useMutation(UpdateProjectDocument);
  const [deleteProject] = useMutation(DeleteProjectDocument);
  const [linkProjectMatch] = useMutation(LinkProjectMatchDocument);
  // Die Antwort trägt Project.matches; der Cache ersetzt damit die Liste, die Kachel verschwindet von selbst.
  const [unlinkProjectMatch] = useMutation(UnlinkProjectMatchDocument);
  const project = query.data?.project ?? null;
  const entity = useMemo(() => project ? {
    ...projectEntity(project, projectRights(project, me.id ?? null, actingAsCompanyId)),
    actingAsCompanyName: activeCompany?.name ?? null,
  } : null, [project, me.id, actingAsCompanyId, activeCompany?.name]);

  return useMemo<DashboardSource>(
    () => ({
      actingAsCompanyId,
      loading: query.loading,
      error: query.error,
      entity,
      save: async (patch) => {
        try {
        const res = await updateProject({ variables: { id, input: { data: patch, ...actingAsDeclaration(actingAsCompanyId) } } });
        throwIfValidationRejected(res.data?.updateProject, "ValidateProjectResult", validationFallback);
        } catch (error) { throw projectWriteError(intl, error, actingAsCompanyId); }
      },
      remove: async () => {
        try {
        await deleteProject({ variables: { input: { id, ...actingAsDeclaration(actingAsCompanyId) } } });
        } catch (error) { throw projectWriteError(intl, error, actingAsCompanyId); }
      },
      linkMatch: async (matchId) => {
        try {
        await linkProjectMatch({ variables: { input: { projectId: id, matchId, ...actingAsDeclaration(actingAsCompanyId) } } });
        } catch (error) { throw projectWriteError(intl, error, actingAsCompanyId); }
      },
      unlinkMatch: async (matchId) => {
        try {
        await unlinkProjectMatch({ variables: { input: { projectId: id, matchId, ...actingAsDeclaration(actingAsCompanyId) } } });
        } catch (error) { throw projectWriteError(intl, error, actingAsCompanyId); }
      },
    }),
    [
      intl,
      actingAsCompanyId,
      deleteProject,
      entity,
      id,
      linkProjectMatch,
      query.error,
      query.loading,
      unlinkProjectMatch,
      updateProject,
      validationFallback,
    ],
  );
}
